import React, { useEffect, useRef } from 'react';
import { AlertCircle, Check, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FilterOption } from '../../../types';

export interface EnumValuesSectionProps {
  options: FilterOption[];
  selectedValues: string[];
  validationErrors?: string[];
  onValueChange: (value: string) => void;
  emptyOptionLabel?: string;
  defaultRender?: (
    option: FilterOption,
    isSelected: boolean
  ) => React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}

/**
 * Walk up from `node` to the nearest ancestor whose computed style makes it a
 * scroll container, and use that as the IntersectionObserver `root`. In this
 * component the sentinel is a direct child of the local `overflow-y-auto`
 * container, so this normally resolves to that container. Falls back to `null`
 * (viewport) if no scroll ancestor is found so the observer still behaves
 * sanely. Resolving the real scroll ancestor (rather than assuming a fixed
 * root) keeps the observer correct even if the surrounding markup changes
 * which element actually clips/scrolls (KIR-2730/KIR-2733 review note).
 */
const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let current: HTMLElement | null = node?.parentElement ?? null;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const renderFilterOption = (
  option: FilterOption,
  isSelected: boolean,
  defaultRender?: (
    option: FilterOption,
    isSelected: boolean
  ) => React.ReactNode,
  emptyOptionLabel = 'None'
): React.ReactNode => {
  if (option.render) {
    return option.render(option, isSelected);
  }

  if (defaultRender) {
    return defaultRender(option, isSelected);
  }

  return (
    <span className="text-sm font-medium flex-1">
      {option.label || emptyOptionLabel}
    </span>
  );
};

/**
 * EnumValuesSection - Multi-select list for enum/list-based filters
 * Used for ENUM column types with INCLUDES/EXCLUDES operators
 */
export const EnumValuesSection: React.FC<EnumValuesSectionProps> = ({
  options,
  selectedValues,
  validationErrors = [],
  onValueChange,
  emptyOptionLabel = 'None',
  defaultRender,
  isLoading = false,
  isError = false,
  onRetry,
  hasMore,
  isFetchingMore,
  onLoadMore,
}) => {
  const errorMessage = validationErrors.find(Boolean);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Load-more trigger: observe a sentinel just past the list end. This works
  // whether the inner container scrolls OR an ancestor (e.g. the Radix popover
  // viewport) is the actual scroll target — the previous `onScroll`/canScroll
  // effect missed the popover-scrolls case, so users saw only the first page
  // (KIR-2730 / KIR-2733).
  useEffect(() => {
    if (!onLoadMore || !hasMore || isFetchingMore || isLoading) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      // No IO in this environment — fall back to eager fetch so pagination
      // still progresses (matches the previous jsdom-only behavior).
      onLoadMore();
      return;
    }

    // Resolve the real scroll ancestor at observe-time — normally the local
    // `overflow-y-auto` container the sentinel lives in. Using an element that
    // actually clips as the IO root keeps `isIntersecting` meaningful, so the
    // sentinel only fires near the bottom instead of being trivially visible.
    const scrollRoot = findScrollParent(sentinel) ?? null;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      { root: scrollRoot, rootMargin: '48px' }
    );
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [options.length, hasMore, isFetchingMore, isLoading, onLoadMore]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col max-h-72 overflow-y-auto overflow-x-hidden"
    >
      {options.length > 0 ? (
        <>
          {isLoading && (
            <div className="flex items-center justify-center py-2 border-b border-border text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
            </div>
          )}
          {options.map(option => {
            const mergedValues = (option.metadata?.mergedValues as
              | string[]
              | undefined) ?? [option.value];
            const isSelected = mergedValues.some(v =>
              selectedValues.includes(v)
            );
            const hasCustomRender = !!(option.render || defaultRender);

            return (
              <button
                key={option.value}
                onClick={() => onValueChange(option.value)}
                className={cn(
                  'flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-accent transition-colors',
                  isSelected && 'bg-accent/50'
                )}
              >
                {!hasCustomRender && (
                  <div className="flex items-center justify-center w-4 h-4">
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {renderFilterOption(
                    option,
                    isSelected,
                    defaultRender,
                    emptyOptionLabel
                  )}
                </div>
              </button>
            );
          })}
        </>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-3 py-6 px-4">
          <div className="flex items-center justify-center size-10 rounded-full bg-destructive/10">
            <AlertCircle className="size-5 text-destructive" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground">
              Failed to load options
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Unable to fetch filter options.
            </p>
          </div>
          {onRetry && (
            <Button
              onClick={onRetry}
              variant="outline"
              size="sm"
              className="mt-1"
            >
              <RefreshCw className="size-3.5 mr-1.5" />
              Retry
            </Button>
          )}
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : (
        <div className="py-4 text-center text-xs text-muted-foreground">
          No values found
        </div>
      )}
      {hasMore && !isError && (
        <div
          ref={sentinelRef}
          aria-hidden="true"
          data-testid="enum-values-load-more-sentinel"
          className="h-px w-full"
        />
      )}
      {isFetchingMore && (
        <div className="flex items-center justify-center py-3 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}
      {errorMessage && (
        <div className="px-3 pb-3 text-xs text-destructive">{errorMessage}</div>
      )}
    </div>
  );
};
